import { useEffect, useState } from "react";
import { SEASON_CONFIG, seasonProgress, type SeasonProgress } from "@/lib/season";
import "@/components/layout/seasonCountdown.css";

/**
 * 상단 메뉴바 바로 아래 고정되는 100-Day IP Race 카운트다운 스트립.
 * 좌→우로 가로지르는 시즌 진행률 바(그라디언트 시머 + 진행 팁 펄스 도트) +
 * 일차/잔여 시간 라벨. 날짜는 lib/season.ts 설정 시임에서만 온다.
 */
export function SeasonCountdownBar() {
  const [progress, setProgress] = useState<SeasonProgress | null>(() =>
    seasonProgress(SEASON_CONFIG),
  );

  useEffect(() => {
    const tick = () => setProgress(seasonProgress(SEASON_CONFIG));
    const id = window.setInterval(tick, 30_000);
    return () => window.clearInterval(id);
  }, []);

  if (!progress) return null;

  const { ratio, day, totalDays, remainingLabel, phase } = progress;
  const pct = Math.round(ratio * 1000) / 10;

  return (
    <div
      className="sticky top-14 z-30 border-b border-line bg-(--ty-bg) lg:top-16"
      aria-label="100-Day IP Race season progress"
    >
      <div className="mx-auto flex w-full max-w-6xl items-center gap-3.5 px-4 py-2 sm:px-6">
        <span className="shrink-0 font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-ink-dim">
          {SEASON_CONFIG.label}
          <span aria-hidden className="mx-1.5 text-accent">
            —
          </span>
          {phase === "upcoming" ? (
            "STARTS SOON"
          ) : (
            <>
              DAY{" "}
              <span className="text-[12px] text-accent">{String(day).padStart(2, "0")}</span>
              <span className="text-ink-mute"> / {totalDays}</span>
            </>
          )}
        </span>

        {/* 좌→우 진행률 트랙 — 시머 필 + 진행 팁 펄스 도트 */}
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
          aria-valuetext={`Day ${day} of ${totalDays}`}
          className="relative h-[5px] min-w-0 flex-1 overflow-visible rounded-full bg-white/10"
        >
          <div
            className="ty-season-fill relative h-full rounded-full transition-[width] duration-700 ease-(--ty-ease)"
            style={{ width: `${pct}%` }}
          >
            {phase === "live" ? <span aria-hidden className="ty-season-tip right-0" /> : null}
          </div>
        </div>

        <span className="shrink-0 font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-ink-dim">
          {phase === "ended" ? (
            "SEASON ENDED"
          ) : (
            <>
              <span className="ty-season-remaining text-[12px] text-accent">{remainingLabel}</span>
              <span className="text-ink-mute">{phase === "upcoming" ? " TO START" : " LEFT"}</span>
            </>
          )}
        </span>
      </div>
    </div>
  );
}
